/* eslint-disable @typescript-eslint/no-explicit-any */

import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { firstValueFrom } from 'rxjs';
import { AttachmentService } from 'src/app/core/services/attachment';
import { Ticket } from 'src/app/models/ticket/ticket.model';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatIconModule, MatProgressSpinnerModule],
})
export class FileUploadComponent {
  readonly fileUploads = viewChild<ElementRef<HTMLInputElement>>('fileUploads');

  readonly allowUserInput = input<boolean>(false);

  readonly ticket = input.required<Ticket>();

  readonly filesUploaded = output<{
    files: { fileName: string; success: boolean }[];
  }>();

  readonly uploadError = signal<unknown | null>(null);

  readonly fileUploadError = computed(() => {
    const err = this.uploadError();
    return err ? this.extractErrorMessage(err) : '';
  });

  readonly isLoading = signal<boolean>(false);

  readonly hasError = signal<boolean>(false);

  readonly ticketId = signal<string>('');

  readonly fileName = signal<string>('');

  readonly isDisabled = computed(
    () => this.ticket().IsClosed || this.allowUserInput(),
  );

  readonly uploadDisabledReason = computed(() => {
    if (this.ticket().IsClosed) {
      return 'File upload is unavailable because this ticket is closed.';
    }

    if (this.allowUserInput()) {
      return 'File upload is unavailable for this ticket state.';
    }

    return '';
  });

  private static readonly ALLOWED_MIME_TYPES = new Set([
    'application/pdf',
    'image/bmp',
    'image/gif',
    'image/jpeg',
    'image/png',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ]);

  private static readonly ALLOWED_EXTENSIONS = new Set([
    'pdf',
    'bmp',
    'gif',
    'jpg',
    'jpeg',
    'png',
    'doc',
    'docx',
  ]);

  readonly acceptedFileTypes = [
    ...FileUploadComponent.ALLOWED_MIME_TYPES,
    ...[...FileUploadComponent.ALLOWED_EXTENSIONS].map((e) => `.${e}`),
  ].join(',');

  private readonly attachmentService = inject(AttachmentService);

  constructor() {
    effect(() => {
      const currentTicket = this.ticket();
      const newTicketId = currentTicket.Id;
      const currentTicketId = this.ticketId();

      if (currentTicketId !== newTicketId) {
        this.ticketId.set(newTicketId);
      }

      // Reset state when ticket changes
      this.uploadError.set(null);
      this.hasError.set(false);
      this.fileName.set('');
    });
  }

  fileUploadClick(): void {
    const fileUploadsElement = this.fileUploads()?.nativeElement;
    if (!fileUploadsElement) {
      return;
    }

    fileUploadsElement.onchange = () => {
      const file = fileUploadsElement.files?.[0];
      if (file !== undefined && this.validateFile(file)) {
        this.sendFile(file);
      }
    };

    fileUploadsElement.click();
  }

  async sendFile(file: File): Promise<void> {
    this.isLoading.set(true);
    this.hasError.set(false);
    this.uploadError.set(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('recordId', this.ticket().Id);
    this.fileName.set(file.name);

    try {
      const success: unknown = await firstValueFrom(
        this.attachmentService.addAttachment(formData),
      );

      this.isLoading.set(false);

      if (
        typeof success === 'object' &&
        success !== null &&
        'message' in success &&
        (success as { message: string }).message ===
          'File uploaded successfully.'
      ) {
        this.hasError.set(false);
        this.filesUploaded.emit({
          files: [{ fileName: file.name, success: true }],
        });
      }
    } catch (error) {
      this.isLoading.set(false);
      this.hasError.set(true);
      this.uploadError.set(error);
    }
  }

  validateFile(file: File): boolean {
    this.hasError.set(false);
    this.uploadError.set(null);

    if (file.size === 0) {
      this.hasError.set(true);
      this.uploadError.set(`File ${file.name} is empty`);
      return false;
    }

    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const mimeOk = FileUploadComponent.ALLOWED_MIME_TYPES.has(file.type);
    const extOk = FileUploadComponent.ALLOWED_EXTENSIONS.has(ext);

    if (!mimeOk && !extOk) {
      this.hasError.set(true);
      this.uploadError.set(`File type ${file.type || ext} is not supported.`);
      return false;
    }

    return true;
  }

  onFileSelected(event: Event): void {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];

    // Reset immediately so selecting the same file again triggers change
    fileInput.value = '';
    if (file && this.validateFile(file)) {
      this.sendFile(file);
    }
  }

  clearSelectedFile(): void {
    this.fileName.set('');
    this.hasError.set(false);
    this.uploadError.set(null);

    const fileInput = this.fileUploads()?.nativeElement;
    if (fileInput) fileInput.value = '';
  }

  private extractErrorMessage(err: unknown): string {
    const fallback =
      'Something went wrong while uploading the document. Please try again.';

    if (typeof err === 'string') return this.normalizeUploadMessage(err);

    if (err instanceof HttpErrorResponse) {
      if (err.status === 0) {
        return 'Network error. Check your connection and try again.';
      }

      // API may send plain text or json
      const raw =
        this.pickMessage(err.error) ||
        this.pickMessage(err.message) ||
        this.pickMessage((err as any)?.statusText) ||
        fallback;

      return this.normalizeUploadMessage(raw);
    }

    const anyErr = err as any;
    const raw =
      this.pickMessage(anyErr?.error) ||
      this.pickMessage(anyErr?.message) ||
      this.pickMessage(anyErr) ||
      fallback;

    return this.normalizeUploadMessage(raw);
  }

  private pickMessage(value: unknown): string | null {
    if (!value) return null;
    if (typeof value === 'string') return value;

    if (typeof value === 'object') {
      const v = value as any;
      return (
        (typeof v.message === 'string' && v.message) ||
        (typeof v.error === 'string' && v.error) ||
        (typeof v.title === 'string' && v.title) ||
        (typeof v.detail === 'string' && v.detail) ||
        null
      );
    }

    return null;
  }

  private normalizeUploadMessage(message: string): string {
    const m = (message || '').trim();
    const lower = m.toLowerCase();

    if (
      lower.includes('file too large') ||
      lower.includes('too large') ||
      lower.includes('exceed') ||
      lower.includes('maximum') ||
      lower.includes('size')
    ) {
      return 'The document exceeds the maximum allowed size.';
    }

    if (lower.includes('forbidden') || lower.includes('special character')) {
      return 'The file name contains forbidden characters. Remove special characters and try again.';
    }

    if (lower.includes('multiple extensions')) {
      return 'The file name contains multiple extensions. Rename the file to use a single extension and try again.';
    }

    if (lower.includes('extension not allowed')) {
      return 'That file type is not allowed. Please upload a PDF or supported image.';
    }

    if (
      lower.includes('content type is not allowed') ||
      lower.includes('file content type')
    ) {
      return 'That file type is not allowed. Please upload a valid PDF or supported image.';
    }

    if (lower.includes('does not match file content')) {
      return 'The file extension does not match the file content. Please rename the file or choose a different file.';
    }

    return m || 'Unknown error occurred';
  }
}
